import { useMemo } from "react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useHotel } from "@/hooks/useHotel";
import {
  useArrivals,
  useDepartures,
  useInHouse,
} from "@/components/pages/settlement/hooks/useSettlement";
import { PreCheckStatus } from "@/components/pages/settlement/types/settlementBooking";
import {
  buildHourlySettlementChartData,
  buildHourlyChartData,
} from "@/components/pages/dashboard/utils/buildHourlySettlementChartData";
import { HourlyBarDatum } from "@/components/pages/dashboard/components/charts/DashboardHourlyBarChart";
import { useRequests } from "@/components/pages/requests/hooks/useRequests";
import { Request, RequestType } from "@/types/request";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

const ALL_RECORDS_PER_PAGE = 500;

function buildInHouseUpsellChartData(
  upsellRequests: Request[],
  inHouseReservationsCount: number,
): HourlyBarDatum[] {
  return buildHourlyChartData(
    upsellRequests,
    (r) => new Date(r.dueDateTime),
    () => true,
    () => false,
  ).map(({ label, primary }) => ({
    label,
    primary,
    secondary: Math.max(inHouseReservationsCount - primary, 0),
    total: inHouseReservationsCount,
  }));
}

export interface DashboardSettlementHourlyChartsResult {
  arrivalsChartData: HourlyBarDatum[];
  departuresChartData: HourlyBarDatum[];
  inHouseChartData: HourlyBarDatum[];
  preCheckinsCount: number;
  preCheckoutsCount: number;
}

export function useDashboardSettlementHourlyCharts(): DashboardSettlementHourlyChartsResult {
  const { hotelId } = useHotel();
  const { capabilities } = useAccessCapabilities();
  const todayDate = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const todayUTCDate = useMemo(
    () => formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd"),
    [],
  );
  const todayStart = useMemo(
    () => `${todayUTCDate}T00:00:00.000Z`,
    [todayUTCDate],
  );
  const todayEnd = useMemo(
    () => `${todayUTCDate}T23:59:59.999Z`,
    [todayUTCDate],
  );

  const { reservations: arrivals } = useArrivals(
    { arrivalDate: todayDate, perPage: ALL_RECORDS_PER_PAGE },
    hotelId,
  );

  const { reservations: departures } = useDepartures(
    { departureDate: todayDate, perPage: ALL_RECORDS_PER_PAGE },
    hotelId,
  );

  const { reservations: inHouseReservations, total: inHouseTotal } = useInHouse(
    { perPage: ALL_RECORDS_PER_PAGE },
    hotelId,
  );

  const { data: upsellRequestsData } = useRequests(
    {
      type: RequestType.GUEST,
      dueDateTimeStart: todayStart,
      dueDateTimeEnd: todayEnd,
      limit: ALL_RECORDS_PER_PAGE,
      hotelId,
    },
    !!hotelId && capabilities.canUseRequestsSystem,
  );

  const arrivalsChartData = useMemo(
    () =>
      buildHourlySettlementChartData(
        arrivals,
        (b) => new Date(b.arrivalDate),
        (b) => b.preCheckinStatus === PreCheckStatus.DONE,
        (b) => b.preCheckinStatus !== PreCheckStatus.DONE,
      ),
    [arrivals],
  );

  const departuresChartData = useMemo(
    () =>
      buildHourlySettlementChartData(
        departures,
        (b) => new Date(b.departureDate),
        (b) => b.preCheckoutStatus === PreCheckStatus.DONE,
        (b) => b.preCheckoutStatus !== PreCheckStatus.DONE,
      ),
    [departures],
  );

  const inHouseChartData = useMemo(() => {
    if (!capabilities.canUseRequestsSystem) {
      return buildInHouseUpsellChartData([], inHouseTotal);
    }

    const upsells = upsellRequestsData?.data ?? [];
    return buildInHouseUpsellChartData(
      upsells,
      inHouseTotal || inHouseReservations.length,
    );
  }, [
    capabilities.canUseRequestsSystem,
    inHouseReservations.length,
    inHouseTotal,
    upsellRequestsData,
  ]);

  const preCheckinsCount = useMemo(
    () =>
      arrivals.filter((b) => b.preCheckinStatus === PreCheckStatus.DONE).length,
    [arrivals],
  );

  const preCheckoutsCount = useMemo(
    () =>
      departures.filter((b) => b.preCheckoutStatus === PreCheckStatus.DONE)
        .length,
    [departures],
  );

  return {
    arrivalsChartData,
    departuresChartData,
    inHouseChartData,
    preCheckinsCount,
    preCheckoutsCount,
  };
}
